import { PaymentMethods } from '../constants/payment';
import { DISCOUNT_TYPES } from '../constants/subscriptions';

import { Product, SubscriptionProductCodes, SubscriptionProductCodeTypes, UpcomingProduct } from './product';

export enum PaymentProviders {
    Inary = 'inary',
    Solidgate = 'solidgate',
}

export interface SubscriptionPause {
    pause_from: string | null;
    pause_to: string | null;
    pause_type: 'pause' | 'free_month' | null;
}

export type UpsellSubscriptionProductType = {
    content_id: string;
    product_code: string;
    discount: number;
    ltv: number;
    start_price: number;
    actual_price: number;
    solid_id: string;
};

export interface IDiscounts {
    discount: string | null;
    pause: string | null;
    free_month: string | null;
    isDiscountOfferAvailable?: boolean;
}

export interface SubscriptionFee {
    fee_enabled: boolean;
    fee_type: 'annual';
    fee_rebill_left: number;
    fee_amount: number;
    fee_total_price: number;
    fee_expired_at: string;
    fee_price: number;
    fee_period: number | null;
    fee_percentage: number | null;
}

export interface ISubscriptionResponse {
    expired_date: string;
    external_id: string;
    id: number;
    price: number;
    trial_price: number;
    product: Product;
    product_code: SubscriptionProductCodes;
    purchase_date: string;
    state: string;
    is_trial: boolean;
    cancelled_at: string;
    payment_provider: PaymentProviders;
    internal_cancel_at?: string;
    upcoming_product: UpcomingProduct | null;
    discount: {
        discount_granted_at: string | null;
        discount_type: DISCOUNT_TYPES;
    };
    discounts: IDiscounts;
    subscription_period: number;
    subscription_trial_period: number | null;
    pause: SubscriptionPause;
    reminder: boolean;
    is_upgrade: boolean;
    billing_cycles: number | null;
    fee: {
        fee_enabled: boolean;
        fee_amount: string;
        fee_total_price: string;
        fee_expired_at: string;
        fee_period: number;
        fee_price: number;
    } | null;
    payment_method: PaymentMethods;
    is_promo_code_used?: boolean;
    product_type: SubscriptionProductCodeTypes;
}

export interface ISubscription extends ISubscriptionResponse {
    contentId: string;
}

export interface IDiscountSubscriptionInfo {
    isSpecialOfferLessExpensive: boolean;
    discount: number;
}

export interface ISubscriptionFeedback {
    reason: string;
    comment: string;
}

export interface ICancelSubscriptionRequest {
    id: string;
    provider: PaymentProviders;
}

export interface IFetchSubscription {
    onSuccess?: (subs: ISubscription[]) => void;
}

export interface ICancelSubscription {
    id: string;
    provider: PaymentProviders;
    onCancel: () => void;
    onSuccess: () => void;
    onError: (errMsg?: string) => void;
}

export interface IChangeSubscription {
    id: number | string;
    product: IChangeSubscriptionProduct;
    discount_type: DISCOUNT_TYPES;
    provider: PaymentProviders;
}

export interface IChangeSubscriptionProduct {
    amount: number;
    currency: string;
    id: number | string;
    name: string;
    subscription_period: number;
    subscription_trial_period: number | null;
    started_from?: string;
    trial: boolean;
    product_id: string;
    price?: number;
    trial_price?: number;
}

export interface IDiscountSubscriptionPrices {
    id: string;
    created_at: string;
    updated_at: string;
    status: string;
    product_price: number;
    trial_price: number;
    currency: string;
    default: boolean;
}

export interface IPeriod {
    unit: string;
    value: number;
}

export type ITrialUpcomingProduct = UpcomingProduct;

export interface IDiscountSubscription {
    id: string;
    name: string;
    created_at: string;
    updated_at: string;
    status: string;
    description: string;
    amount: number;
    currency: string;
    started_from: string;
    product_code: SubscriptionProductCodes;
    prices: IDiscountSubscriptionPrices[];
    subscription_period: number;
    subscription_trial_period: number | null;
    trial: boolean;
    product_id: string;
    price?: number;
    trial_price?: number;
    payment_provider: PaymentProviders;
}

export interface ISubscriptionBase {
    id: string;
    provider: PaymentProviders;
    onSuccess?: () => void;
    onError?: () => void;
}

export interface IFetchDiscountSubscription extends ISubscriptionBase {
    discount_type: DISCOUNT_TYPES;
    subscriptionsAvailableForOffer?: ISubscription[];
    provider: PaymentProviders;
}

export interface IReturnSubscription extends ISubscriptionBase {
    product: Partial<IDiscountSubscription>;
    discount_type: DISCOUNT_TYPES;
}

export type IPauseSubscription = {
    id: string;
    provider: PaymentProviders;
    onSuccess?: (subs: ISubscription[]) => void;
    onError?: () => void;
};

export type IResumeSubscription = {
    id: string;
    provider: PaymentProviders;
    onSuccess?: (subs: ISubscription[]) => void;
    onError?: () => void;
};

export type IChangePlan = {
    plan: string;
    onSuccess?: () => void;
    onError?: () => void;
};

export type IRestoreSubscription = {
    id: string;
    provider: PaymentProviders;
    onSuccess?: () => void;
    onError?: () => void;
};

export type IReminderOffer = {
    external_id: string;
    provider: PaymentProviders;
    onSuccess?: () => void;
    onError?: () => void;
};

export interface IDiscountsForSubscriptions {
    [externalID: string]: {
        [discountType in DISCOUNT_TYPES]?: {
            subscription: IDiscountSubscription;
        };
    };
}
